import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Col, Modal, Row, Button, Radio, Input, Form, Select } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import { CheckOutlined } from '@ant-design/icons';

import {
  FilterTypeEnum,
  ListStockItemsQuery,
  StockItem,
  StockItemType,
  StockOrderColumnConfig,
} from '@/core/domain/stock/StockItems/StockItems.types';

import { useStockItems } from '@/core/domain/stock/StockItems/StockItems.hooks';

import { ModalProps } from '@/types/ModalProps';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import { getRowKey } from '@/core/domain/stock/StockItems/StockItems.utils';
import { SorterResult } from 'antd/lib/table/interface';
import { tableSorterOrder } from '@/utils/table';
import { PartItemFieldOption } from '@/core/domain/stock/Configuration/Configuration.types';
import InfiniteTable from '../InfiniteTable';
import FiltersItemsStockModal from './FiltersItemsStockModal';
import { quantityFormatter } from '@/utils/number';
import {
  RequestPurchaseColumnConfig,
  RequestPurchaseFieldOption,
  RequestPurchases,
  RequestPurchasesQuery,
} from '@/core/domain/workorder/RequestPurchase/RequestPurchase.types';
import { useListRequestPurchases } from '@/core/domain/workorder/RequestPurchase/RequestPurchase.hooks';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import StatusTag from '@/components/_common/StatusTag';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import {
  RegistrationsDefaultCode,
  RegistrationsDefaultCodeLabel,
} from '@/types/Register';
import { flatInfiniteResult } from '@/utils/request/flatInfiniteResult';
import { useDebounce } from '@/hooks';

enum RadioSelected {
  Parts = 'parts',
  Consumables = 'consumables',
  OS = 'os',
}

interface Props extends ModalProps {
  itemType?: StockItemType;
  onSelectItem: (item?: StockItem, itemOs?: RequestPurchases) => void;
  itemsSelected?: { partId: number; complementId?: number }[];
  companyId?: number;
}

const ItemsSelectedModal: React.FC<Props> = ({
  visible,
  handleVisible,
  itemType,
  onSelectItem,
  companyId,
  itemsSelected,
}: Props) => {
  const intl = useIntl();
  const [radioSelected, setRadioSelected] = useState<RadioSelected>(
    RadioSelected.Parts,
  );

  const [workOrder, setWorkOder] = useState<boolean>();

  const [queryParams, setQueryParams] = useState<ListStockItemsQuery>({
    itemType: StockItemType.Part,
  });

  const queryDebounce = useDebounce(queryParams);

  const { data, isFetching, hasNextPage, fetchNextPage } = useStockItems(
    visible,
    { ...queryDebounce, active: true },
  );

  const stockItems = flatInfiniteResult(data);

  const { data: matrix } = useMatrix();

  const defaultPartCode = matrix?.matrixSettings.defaultPartCode;

  const [queryParamsRequestPurchases, setQueryParamsRequestPurchases] =
    useState<RequestPurchasesQuery>({
      order: 'DESC',
      companyIdFilter: companyId,
    });

  const queryDebouncePurchases = useDebounce(queryParamsRequestPurchases);

  const {
    data: requestPurchasesData,
    isFetching: requestPurchasesFetching,
    hasNextPage: requestPurchasesHasNextPage,
    fetchNextPage: requestPurchasesFetchNextPage,
  } = useListRequestPurchases(
    queryDebouncePurchases,
    radioSelected === RadioSelected.OS,
  );

  const requestPurchases = flatInfiniteData(requestPurchasesData);

  useEffect(() => {
    if (radioSelected) {
      setWorkOder(false);

      if (radioSelected === RadioSelected.OS) {
        setWorkOder(true);
      } else {
        setQueryParams({
          itemType:
            radioSelected === RadioSelected.Parts
              ? StockItemType.Part
              : StockItemType.Consumable,
        });
      }
    }
  }, [radioSelected]);

  const handleCancel = () => {
    handleVisible();
    setRadioSelected(RadioSelected.Parts);
    setQueryParams({
      itemType: StockItemType.Part,
    });
    setQueryParamsRequestPurchases({
      order: 'DESC',
      companyIdFilter: companyId,
    });
  };

  function handleOrderStockItem(sorter: SorterResult<StockItem>) {
    if (sorter && sorter?.columnKey) {
      setQueryParams((prev) => {
        if (sorter && sorter?.columnKey) {
          const order = sorter.order
            ? tableSorterOrder[sorter.order]
            : undefined;

          if (prev.orderColumns === StockOrderColumnConfig[sorter.columnKey]) {
            return {
              ...prev,
              order,
            };
          }

          return {
            ...prev,
            order: tableSorterOrder.ascend,
            orderColumns: StockOrderColumnConfig[sorter.columnKey],
          };
        }

        return prev;
      });
    }
  }

  function handleOrderRequestPurchase(sorter: SorterResult<RequestPurchases>) {
    if (sorter && sorter?.columnKey) {
      setQueryParamsRequestPurchases((prev) => {
        if (sorter && sorter?.columnKey) {
          const order = sorter.order
            ? tableSorterOrder[sorter.order]
            : undefined;

          if (
            prev.orderByPurchaseQuotation ===
            RequestPurchaseColumnConfig[sorter.columnKey]
          ) {
            return {
              ...prev,
              order,
              companyIdFilter: companyId,
            };
          }

          return {
            ...prev,
            order: tableSorterOrder.ascend,
            orderByPurchaseQuotation:
              RequestPurchaseColumnConfig[sorter.columnKey],
            companyIdFilter: companyId,
          };
        }

        return prev;
      });
    }
  }

  const columns: ColumnsType<StockItem> = [
    radioSelected === RadioSelected.Parts
      ? useConfigurationColumnsPart({ internalCode: 'sku' })
      : useConfigurationColumnsPart({}),
    {
      ellipsis: true,
      dataIndex: 'description',
      key: PartItemFieldOption.Description,
      title: intl.formatMessage({
        id: 'stockCorrections.domain.items.itemName',
      }),
      width: 300,
      sorter: true,
    },
    {
      ellipsis: true,
      width: 120,
      title: intl.formatMessage({
        id: 'stockCorrections.domain.items.type',
      }),
      render(row: StockItem) {
        let label = '-';

        if (row.partId) {
          label = intl.formatMessage({
            id: 'stockCorrections.domain.items.type.item_type.part',
          });
        } else if (row.consumableId) {
          label = intl.formatMessage({
            id: 'stockCorrections.domain.items.type.item_type.consumable',
          });
        }

        return <StatusTag label={label} color="blue" />;
      },
    },
    {
      dataIndex: 'actions',
      render: (_, row) => (
        <Button
          type="primary"
          onClick={() => onSelectItem(row)}
          icon={<CheckOutlined />}
        />
      ),
      width: 90,
      fixed: 'right',
      align: 'right',
    },
  ];

  const columnsWorkOrder: ColumnsType<RequestPurchases> = [
    {
      ellipsis: true,
      width: 140,
      dataIndex: 'workOrderNumber',
      key: RequestPurchaseFieldOption.workOrder,
      title: intl.formatMessage({
        id: 'commons.table.column.workOrder',
      }),
      sorter: true,
    },
    {
      ellipsis: true,
      dataIndex: 'description',
      key: RequestPurchaseFieldOption.description,
      title: intl.formatMessage({
        id: 'commons.table.column.description',
      }),
      width: 300,
      sorter: true,
    },
    useConfigurationColumnsPart({
      internalCode: ['item', 'sku'],
      manufacturingCode: ['item', 'manufacturingCode'],
      barcode: ['item', 'barCode'],
      companyCode: ['item', 'companyCode'],
      isRequestPurchase: true,
    }),
    {
      dataIndex: 'requestedQuantity',
      key: RequestPurchaseFieldOption.quantity,
      ellipsis: true,
      width: 140,
      title: intl.formatMessage({
        id: 'commons.table.column.qtyRequested',
      }),
      render: (value) => quantityFormatter(value),
      sorter: true,
    },
    {
      dataIndex: 'actions',
      align: 'right',
      render: (_, row) => (
        <Button
          type="primary"
          onClick={() => onSelectItem(undefined, row)}
          icon={<CheckOutlined />}
          disabled={itemsSelected?.some(
            (item) =>
              item.partId === row?.item?.id &&
              item.complementId === row.complementId,
          )}
        />
      ),
      width: 90,
    },
  ];

  const handleDefaultPartCode = (
    codeType: RegistrationsDefaultCode,
    value: string,
  ) => {
    switch (codeType) {
      case 'MANUFACTURER_CODE':
        return { manufacturingCode: value };

      case 'COMPANY_CODE':
        return { companyCode: value };

      case 'BARCODE':
        return { barcode: value };

      case 'INTERNAL_CODE':
        return { sku: value };

      default:
        return {};
    }
  };

  return (
    <Modal
      title={intl.formatMessage({ id: 'stockCorrections.domain.items.add' })}
      open={visible}
      onCancel={handleCancel}
      width={950}
      okButtonProps={{ style: { display: 'none' } }}
      cancelText={intl.formatMessage({ id: 'commons.form.button.close' })}
      destroyOnClose
    >
      <Row gutter={16}>
        <Col span={24}>
          <FiltersItemsStockModal
            setQueryParams={
              workOrder ? setQueryParamsRequestPurchases : setQueryParams
            }
            itemType={itemType}
            extraFilters={
              workOrder ? (
                <Row gutter={8}>
                  <Col span={24} md={12}>
                    <Input
                      placeholder="Código do fabricante"
                      value={
                        queryParamsRequestPurchases?.manufacturingCodeFilter
                      }
                      onChange={({ target: { value } }) =>
                        setQueryParamsRequestPurchases((prev) => ({
                          ...prev,
                          manufacturingCodeFilter: value,
                        }))
                      }
                    />
                  </Col>
                </Row>
              ) : (
                <Row gutter={[16, 16]}>
                  <Col span={24} sm={8}>
                    <Input
                      onChange={({ target: { value } }) =>
                        setQueryParams((prev) => ({
                          ...prev,
                          ...(defaultPartCode &&
                            handleDefaultPartCode(defaultPartCode, value)),
                        }))
                      }
                      className="search-filter-input"
                      placeholder={
                        defaultPartCode
                          ? RegistrationsDefaultCodeLabel[defaultPartCode]
                          : ''
                      }
                    />
                  </Col>

                  <Col span={24} sm={8}>
                    <Form.Item
                      noStyle
                      name="filterType"
                      initialValue={FilterTypeEnum.free}
                    >
                      <Select
                        style={{ width: '100%' }}
                        options={[
                          {
                            label: 'Por campo - Livre',
                            value: FilterTypeEnum.free,
                          },
                          {
                            label: 'Por campo - Especifico',
                            value: FilterTypeEnum.specific,
                          },
                        ]}
                        onChange={(value) =>
                          setQueryParams((prev) => ({
                            ...prev,
                            filterType: value,
                          }))
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )
            }
          />
        </Col>

        <Col>
          <div>Filtrar por:</div>

          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            size="large"
            onChange={({ target }) => setRadioSelected(target.value)}
            defaultValue={radioSelected}
            style={{ marginBottom: 20 }}
          >
            <Radio value={RadioSelected.Parts}>Peças</Radio>
            <Radio value={RadioSelected.Consumables}>Insumos</Radio>
            <Radio value={RadioSelected.OS}>Peça O.S.</Radio>
          </Radio.Group>
        </Col>

        <Col flex="1">
          <div style={{ width: '100%' }}>
            {!workOrder ? (
              <InfiniteTable<StockItem>
                columns={columns}
                onChange={(_p, _f, sorter) => {
                  if (!Array.isArray(sorter)) {
                    handleOrderStockItem(sorter);
                  }
                }}
                hasNextPage={hasNextPage}
                fetchNextPage={fetchNextPage}
                dataSource={stockItems}
                loading={isFetching}
                className="scroll-table"
                rowKey={(row) => getRowKey(row)}
              />
            ) : (
              <InfiniteTable<RequestPurchases>
                columns={columnsWorkOrder}
                onChange={(_p, _f, sorter) => {
                  if (!Array.isArray(sorter)) {
                    handleOrderRequestPurchase(sorter);
                  }
                }}
                hasNextPage={requestPurchasesHasNextPage}
                fetchNextPage={requestPurchasesFetchNextPage}
                dataSource={requestPurchases}
                loading={requestPurchasesFetching}
                className="scroll-table"
                rowKey={(row) => row.id}
              />
            )}
          </div>
        </Col>
      </Row>
    </Modal>
  );
};

export default ItemsSelectedModal;
